import React from 'react';
import { Helmet } from 'react-helmet-async';

const TermsPage: React.FC = () => {
  const lastUpdated = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <>
      <Helmet>
        <title>Terms of Service - AgenTrav</title>
        <meta name="description" content="Read the Terms of Service for using AgenTrav. Understand your rights and responsibilities when using our platform." />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-b from-black via-gray-950 to-black text-neutral-300 p-4 sm:p-8">
        <div className="max-w-4xl mx-auto bg-white/5 border border-white/10 rounded-lg shadow-lg p-6 sm:p-10 prose prose-invert prose-sm sm:prose-base prose-headings:text-white prose-headings:font-semibold prose-a:text-blue-400 hover:prose-a:text-blue-300 prose-strong:text-neutral-100">
          <h1 className="text-2xl sm:text-3xl font-bold mb-6 text-white">Terms of Service</h1>
          <p className="text-xs sm:text-sm text-neutral-400 mb-6">Last updated: {lastUpdated}</p>

          <section className="mb-6">
            <h2 className="text-xl sm:text-2xl font-semibold mb-3 text-white">1. Introduction</h2>
            <p>
              Welcome to AgenTrav! These Terms of Service ("Terms") govern your access to and use of the AgenTrav website, applications, and services (collectively, the "Service"). Please read these Terms carefully before using the Service. By accessing or using the Service, you agree to be bound by these Terms.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl sm:text-2xl font-semibold mb-3 text-white">2. User Accounts</h2>
            <p>
              To access certain features of the Service, you may be required to create an account. You are responsible for safeguarding your account information, including your password, and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account.
            </p>
            {/* Add specifics about different user types (Hotel, Agent, Traveler) if needed */}
          </section>

          <section className="mb-6">
            <h2 className="text-xl sm:text-2xl font-semibold mb-3 text-white">3. Use of the Service</h2>
            <p>
              You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree not to use the Service:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>In any way that violates any applicable federal, state, local, or international law or regulation.</li>
              <li>To transmit, or procure the sending of, any advertising or promotional material, including any "junk mail", "chain letter," "spam," or any other similar solicitation.</li>
              <li>To impersonate or attempt to impersonate AgenTrav, an AgenTrav employee, another user, or any other person or entity.</li>
              <li>To engage in any other conduct that restricts or inhibits anyone's use or enjoyment of the Service, or which, as determined by us, may harm AgenTrav or users of the Service or expose them to liability.</li>
            </ul>
             {/* Add specific rules for Hotels and Agents regarding content, listings, communications etc. */}
          </section>

          <section className="mb-6">
            <h2 className="text-xl sm:text-2xl font-semibold mb-3 text-white">4. Content Ownership and Intellectual Property</h2>
            <p>
              The Service and its original content (excluding content provided by users), features, and functionality are and will remain the exclusive property of AgenTrav and its licensors. Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of AgenTrav.
            </p>
            <p>
              Content you submit or upload to the Service (e.g., hotel descriptions, agent profiles, reviews) remains your property. However, by submitting content, you grant AgenTrav a worldwide, non-exclusive, royalty-free license to use, copy, reproduce, process, adapt, modify, publish, transmit, display, and distribute such content in any and all media or distribution methods.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl sm:text-2xl font-semibold mb-3 text-white">5. Disclaimers</h2>
            <p>
              The Service is provided on an "AS IS" and "AS AVAILABLE" basis. AgenTrav makes no representations or warranties of any kind, express or implied, as to the operation of their services, or the information, content, or materials included therein. You expressly agree that your use of the Service is at your sole risk.
            </p>
             {/* Add specific disclaimers about accuracy of listings, agent advice, etc. */}
          </section>

          <section className="mb-6">
            <h2 className="text-xl sm:text-2xl font-semibold mb-3 text-white">6. Limitation of Liability</h2>
            <p>
              In no event shall AgenTrav, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your access to or use of or inability to access or use the Service.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl sm:text-2xl font-semibold mb-3 text-white">7. Governing Law</h2>
            <p>
              These Terms shall be governed and construed in accordance with the laws of the Republic of Maldives, without regard to its conflict of law provisions.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl sm:text-2xl font-semibold mb-3 text-white">8. Changes to Terms</h2>
            <p>
              We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion. By continuing to access or use our Service after any revisions become effective, you agree to be bound by the revised terms.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl sm:text-2xl font-semibold mb-3 text-white">9. Contact Us</h2>
            <p>
              If you have any questions about these Terms, please contact us at [Your Contact Email or Link to Contact Form].
            </p>
          </section>
        </div>
      </div>
    </>
  );
};

export default TermsPage; 